import DownloadRounded from "@mui/icons-material/DownloadRounded";
import FormatQuoteRounded from "@mui/icons-material/FormatQuoteRounded";
import OpenInNewRounded from "@mui/icons-material/OpenInNewRounded";
import {
    Button,
    Grid,
    IconButton,
    Tooltip,
    Typography,
    useTheme,
} from "@mui/material";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import { Routes } from "components/Navigation/Routes";
import { durationDisplay } from "components/SoundClipEditor/Controls/Player";
import AcxMicroPlayer from "components/UI/Audio/AcxMicroPlayer";
import { observer } from "mobx-react";
import moment from "moment";
import AudioContextProvider from "providers/AudioContextProvider";
import React, { useState } from "react";
import { useNavigate } from "react-router";
import FilesService from "services/FilesService";
import { AuthStore } from "stores/AuthStore";
import { useStore } from "utils/useStore";
import { ActionProps } from "./Actions";

export interface ShownSoundClip {
    number: number;
    soundClipId: string;
    conversationId: string;
    quote: string;
    startTime: number;
    endTime: number;
    callDate?: string | null;
    speaker?: string | null;
}

/**
 * Shows the clips KBo created or found as cards that play inline, with View and download.
 */
export const SoundClipsAction: React.FC<ActionProps> = observer(({ args }) => {
    const authStore = useStore(AuthStore);
    const clips = (args as { clips?: ShownSoundClip[] } | undefined)?.clips;

    // Same media and download guards as the montage player
    const isUltra = authStore.isUserUltra();
    const userCannotViewMedia =
        !isUltra && authStore.canUserView("Restrict Media");
    const canDownload = isUltra || !authStore.canUserView("Restrict Downloads");

    if (!clips?.length || userCannotViewMedia) return null;

    return (
        <Grid container direction="column" gap={1} width="100%">
            {clips.map((clip) => (
                <SoundClipCard
                    key={clip.soundClipId}
                    clip={clip}
                    canDownload={canDownload}
                />
            ))}
        </Grid>
    );
});

const SoundClipCard: React.FC<{
    clip: ShownSoundClip;
    canDownload: boolean;
}> = observer(({ clip, canDownload }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const messageStore = useStore(MessageStore);
    const [isDownloading, setIsDownloading] = useState(false);

    async function download() {
        setIsDownloading(true);
        try {
            const blob = await new FilesService().downloadSoundClip(
                clip.soundClipId,
            );
            new AudioContextProvider().download(
                blob,
                `KBo clip ${clip.number}.mp3`,
                false,
            );
        } catch {
            messageStore.logError("Unable to download the clip.");
        } finally {
            setIsDownloading(false);
        }
    }

    const details = [
        `Clip ${clip.number}`,
        clip.speaker,
        clip.callDate ? moment(clip.callDate).format("MMM D, YYYY") : null,
    ]
        .filter(Boolean)
        .join(" · ");

    return (
        <Grid
            container
            direction="column"
            gap={1}
            padding={theme.spacing(2)}
            sx={{
                border: `1px solid ${theme.palette.gray[200]}`,
                borderRadius: 2,
                backgroundColor: theme.palette.white.main,
            }}
        >
            <Grid container item gap={1} wrap="nowrap" alignItems="flex-start">
                {/* MUI only has a closing quote mark; turned around, it opens the quote */}
                <FormatQuoteRounded
                    fontSize="small"
                    sx={{
                        color: theme.palette.gray[400],
                        flexShrink: 0,
                        transform: "rotate(180deg)",
                    }}
                />
                <Typography fontSize="14px" flexGrow={1}>
                    {clip.quote}
                </Typography>
                {canDownload && (
                    <Tooltip title="Download">
                        <span>
                            <IconButton
                                aria-label={`Download clip ${clip.number}`}
                                size="small"
                                disabled={isDownloading}
                                onClick={download}
                                sx={{ color: theme.palette.blue.main }}
                            >
                                <DownloadRounded fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
            </Grid>
            <Grid
                container
                item
                alignItems="center"
                justifyContent="space-between"
                wrap="nowrap"
                gap={1}
                paddingLeft="28px"
            >
                <Typography
                    fontSize="13px"
                    color={theme.palette.gray[400]}
                    noWrap
                >
                    {details}
                </Typography>
                <Grid
                    container
                    item
                    alignItems="center"
                    justifyContent="flex-end"
                    wrap="nowrap"
                    width="auto"
                    flexShrink={0}
                >
                    <AcxMicroPlayer
                        downloadMediaFileId={clip.soundClipId}
                        mediaSourceType="soundClip"
                    />
                    <Typography fontSize="13px" color={theme.palette.gray[400]}>
                        {durationDisplay(
                            Math.max(0, clip.endTime - clip.startTime),
                        )}
                    </Typography>
                    <Button
                        size="small"
                        endIcon={<OpenInNewRounded fontSize="small" />}
                        onClick={() =>
                            navigate(
                                Routes.makeConversationsRoute(
                                    clip.conversationId,
                                ),
                            )
                        }
                        sx={{ marginLeft: 1, textTransform: "none" }}
                    >
                        View
                    </Button>
                </Grid>
            </Grid>
        </Grid>
    );
});
